import React, { useEffect } from 'react'
import {
  Grid2 as Grid,
  Typography,
  Box,
  Button,
  Paper,
  styled
} from '@mui/material'
import { GenericTableV2 } from '../../../components/_EOS/GenericTable/GenericTableV2'
import { getOrderSkus, getOrderSummary, getOrderBlog } from '../config/api'
import {
  OrderBlog,
  OrderSkuSummary,
  OrderSummary
} from '../../../interfaces/Order/Order'
import { TableColumn } from '../../../components/_EOS/GenericTable'
import { useApi } from '../../../hooks/useApi'
import { usePagination } from '../../../hooks/usePagination'
import { ApiResponse } from '../../../typings/ApiResponse'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { eOrderStates } from '../../../enums/eOrderStates'
import { utcToLocal } from '../../../utils/dateHelper'

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '96%',
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2)
}))

interface OrderDetailModalProps {
  orderId: number
  onClose: () => void
  handleSnackbar: (message: string, success: boolean) => void
}

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  orderId,
  onClose,
  handleSnackbar
}) => {
  const { callApi: requestGetSku, state: stateSku } =
    useApi<ApiResponse<OrderSkuSummary>>()
  const { callApi: requestGetOrder, state: stateOrder } = useApi<OrderSummary>()
  const { callApi: requestGetOrderBlog, state: stateOrderBlog } =
    useApi<OrderBlog[]>()
  const pagination = usePagination(5)

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    pagination.setPagination(auxPage, pageSize)
  }

  const requestDataOrder = () => {
    requestGetOrder(getOrderSummary(orderId)).catch((error) => {
      handleSnackbar(error, false)
    })
  }

  const requestDataOrderBlog = () => {
    requestGetOrderBlog(getOrderBlog(orderId)).catch((error) => {
      handleSnackbar(error, false)
    })
  }

  const requestDataSku = (page: number, pageSize: number) => {
    requestGetSku(getOrderSkus(orderId, page, pageSize)).catch((error) => {
      handleSnackbar(error, false)
    })
  }

  useEffect(() => {
    requestDataOrder()
    requestDataOrderBlog()
  }, [])

  useEffect(() => {
    requestDataSku(pagination.page, pagination.pageSize)
  }, [pagination.page, pagination.pageSize])

  const skuColumns: TableColumn<OrderSkuSummary>[] = [
    {
      key: 'sku',
      title: 'SKU/EAN'
    },
    {
      key: 'name',
      title: 'Descripción'
    },
    {
      key: 'qty',
      title: 'Cantidad'
    }
  ]

  const logColumns: TableColumn<OrderBlog>[] = [
    {
      key: 'createdDate',
      title: 'Fecha',
      render: (value: unknown) =>
        utcToLocal(value as string, 'dd/MM/yyyy HH:mm:ss')
    },
    {
      key: 'orderStateId',
      title: 'Estado',
      render: (value: unknown) => eOrderStates[value as number] || ''
    },
    {
      key: 'comments',
      title: 'Comentarios'
    },
    {
      key: 'userName',
      title: 'Usuario'
    }
  ]

  if (
    stateSku.status === Status.Loading ||
    stateOrder.status === Status.Loading ||
    stateOrderBlog.status === Status.Loading
  ) {
    return <Loading />
  }

  return (
    <Box p={2}>
      <Box mb={2}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 4 }}>
            <Typography variant='body2'>
              <Box component='span' fontWeight='bold'>
                ID Interno:{' '}
              </Box>
              {orderId}
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography variant='body2'>
              <Box component='span' fontWeight='bold'>
                ID de Pedido:{' '}
              </Box>
              {stateOrder?.data?.referenceId || 'Sin Información'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography variant='body2'>
              <Box component='span' fontWeight='bold'>
                Marca:{' '}
              </Box>
              {stateOrder?.data?.productOrigins || 'Sin Información'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography variant='body2'>
              <Box component='span' fontWeight='bold'>
                Canal de venta:{' '}
              </Box>
              {stateOrder?.data?.orderOrigin || 'Sin Información'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography variant='body2'>
              <Box component='span' fontWeight='bold'>
                Courier:{' '}
              </Box>
              {stateOrder?.data?.courier || 'Sin Información'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography variant='body2'>
              <Box component='span' fontWeight='bold'>
                Servicio Courier:{' '}
              </Box>
              {stateOrder?.data?.courierService || 'Sin Información'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <StyledPaper variant='outlined'>
        <Box mb={1}>
          <Typography
            variant='subtitle1'
            gutterBottom
            style={{ fontWeight: 'bold' }}
          >
            Items
          </Typography>
        </Box>
        <Box mb={1}>
          <GenericTableV2
            data={stateSku.data?.items || []}
            columns={skuColumns}
            keyField='orderDetailId'
            pagination={{
              page: pagination.page,
              pageSize: pagination.pageSize,
              total: stateSku.data?.totalItems || 0,
              onPageChange: handlePagination
            }}
            styling={{
              dense: true,
              hoverEffect: true,
              alternatingRows: true
            }}
          />
        </Box>
      </StyledPaper>

      <StyledPaper variant='outlined'>
        <Box mb={1}>
          <Typography
            variant='subtitle1'
            gutterBottom
            style={{ fontWeight: 'bold' }}
          >
            Bitácora
          </Typography>
        </Box>
        <Box mb={1}>
          <GenericTableV2
            data={stateOrderBlog?.data || []}
            columns={logColumns}
            keyField='orderBlogId'
            styling={{
              dense: true,
              hoverEffect: true,
              alternatingRows: true
            }}
            emptyMessage='Sin Registros'
          />
        </Box>
      </StyledPaper>

      <Box display='flex' justifyContent='flex-end' mt={2}>
        <Button variant='contained' color='primary' onClick={onClose}>
          CERRAR
        </Button>
      </Box>
    </Box>
  )
}
